import { useEditorStore } from '@/stores/editor'
import { storeToRefs } from 'pinia'
import { nextTick } from 'vue'
import { useCommonStore } from '@/stores/common.ts'
import { checkBlock, deletePrefix } from '@/views/editor/composable/useNoteUtils'
import { newBlockEvent } from '@/views/editor/composable/useBlockEvent'
import { setTableData, setTableTextData } from '@/views/editor/composable/useBlockCreate'
import { UseSelection } from '@/views/editor/composable/useSelection.ts'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable.ts'
import { restoreLatexElements } from '@/views/editor/composable/useLatexRestore.ts'

// 최근 페이스트가 완료된 블록을 추적하여 서버 업데이트 중복 방지
// 형식: "noteUid-blockUid" (다중 노트 환경에서 충돌 방지)
export const recentlyPastedBlocks = new Set<string>()

function convertNewlinesToBr(element: HTMLElement) {
	const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
	const textNodes: Text[] = []
	let node: Text | null
	while ((node = walker.nextNode() as Text | null)) {
		if (node.textContent?.includes('\n')) {
			textNodes.push(node)
		}
	}
	textNodes.forEach(tn => {
		let text = tn.textContent!

		// 텍스트 노드 앞뒤의 \n은 Windows HTML 직렬화 아티팩트 → 제거
		text = text.replace(/^\n+/, '').replace(/\n+$/, '')

		if (!text) {
			tn.textContent = ''
			return
		}

		if (!text.includes('\n')) {
			tn.textContent = text
			return
		}

		// 텍스트 중간의 \n은 실제 줄바꿈 → <br>로 변환
		const parts = text.split('\n')
		const frag = document.createDocumentFragment()
		parts.forEach((part, idx) => {
			if (idx > 0) frag.appendChild(document.createElement('br'))
			if (part) frag.appendChild(document.createTextNode(part))
		})
		tn.parentNode?.replaceChild(frag, tn)
	})
}

async function getImageDimensions(imgSrc: string, imgElement?: HTMLImageElement): Promise<{ width: number; height: number }> {
	if (imgElement && imgElement.width && imgElement.height) {
		return { width: imgElement.width, height: imgElement.height }
	}

	return new Promise(resolve => {
		const img = new Image()
		img.onload = () => {
			resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height })
		}
		img.onerror = () => {
			resolve({ width: 360, height: 0 })
		}
		img.src = imgSrc
	})
}

export const usePaste = () => {
	async function onPaste(event: any) {
		event.preventDefault()
		event.stopPropagation()

		const { socket } = storeToRefs(useCommonStore())
		const { isPasting, noteType } = storeToRefs(useEditorStore())

		if (isPasting.value) {
			return
		}

		await new UseHistory().push()

		isPasting.value = true

		let lastBlockToFocus: any = null

		const { selector } = storeToRefs(useEditorStore())

		try {
			const selection: any = window.getSelection()
			if (!selection?.rangeCount) {
				return
			}

			const parser: DOMParser = new DOMParser()
			const clipBoard: any = navigator.clipboard
			const clipboardData = event.clipboardData

			// 안전한 순회를 위해 Array.from 사용
			const items = clipboardData?.items ? Array.from(clipboardData.items) : []

			const imageList: any = []

			items.forEach((item: any) => {
				if (item.type.includes('image')) {
					const image = item.getAsFile()
					if (image) {
						const imageUrl = URL.createObjectURL(image)
						imageList.push(imageUrl)
					}
				}
			})

			if (!selector.value?.id) {
				return
			}

			// 먼저 event.clipboardData에서 HTML/TEXT를 우선 시도
			let htmlElementBlob: string | undefined
			const htmlFromEvent = clipboardData?.getData ? clipboardData.getData('text/html') : ''
			let textFromEvent = clipboardData?.getData ? clipboardData.getData('text/plain') : ''

			if (htmlFromEvent && htmlFromEvent.trim() !== '') {
				htmlElementBlob = htmlFromEvent
			} else {
				// 필요 시에만 navigator.clipboard.read() 시도 (권한/환경 예외 대비)
				try {
					if (clipBoard?.read) {
						const readHTML: any = await clipBoard.read()
						const htmlItem = readHTML.find((item: any) => item.types?.includes?.('text/html'))
						const HTMLBlob: Blob | undefined = await htmlItem?.getType?.('text/html')
						htmlElementBlob = (await HTMLBlob?.text()) as string | undefined
					}
				} catch (e) {}
			}

			// selector.value.id가 temp-인 경우 DOM에서 마지막 유효 블록으로 fallback
			// Template 모드에서는 temp- 블록이 영구적이므로 fallback하지 않음
			let resolvedSelectorId = selector.value.id
			if (resolvedSelectorId.startsWith('temp-') && noteType.value === 'Project') {
				const validBlocks = [...document.querySelectorAll('.editor--block')].filter((b: any) => !b.id.startsWith('temp-'))
				const lastValid: any = validBlocks[validBlocks.length - 1]
				if (lastValid) resolvedSelectorId = lastValid.id
			}
			let targetUid = deletePrefix(resolvedSelectorId)
			const blockData: any = []

			let block: any = document.getElementById(resolvedSelectorId) as HTMLElement
			let newBlockContainer: any = block
			let initialContainer = block?.closest('.editor--block__container')

			const hasEditorBlockHtml = htmlElementBlob && htmlElementBlob.includes('editor--block')

			const insertPlainTextLines = async (text: string) => {
				const { noteUid } = storeToRefs(useEditorStore())
				const currentBlock: any = document.getElementById(selector.value.id) as HTMLElement
				let textBlockContainer: any = currentBlock

				const entersArray = text.split(/\r?\n/)

				let tailHtml = ''
				if (entersArray.length > 1 && selection.rangeCount > 0) {
					const caretRange = selection.getRangeAt(0)
					const tailRange = caretRange.cloneRange()
					if (currentBlock.lastChild) {
						tailRange.setEndAfter(currentBlock.lastChild)
					} else {
						tailRange.setEnd(currentBlock, currentBlock.childNodes.length)
					}
					const container = document.createElement('div')
					container.appendChild(tailRange.cloneContents())
					tailHtml = container.innerHTML
					tailRange.deleteContents()
				}

				for (let index = 0; index < entersArray.length; index++) {
					const line = entersArray[index]
					if (index === 0) {
						if (selection.rangeCount > 0) {
							const range = selection.getRangeAt(0)
							const textNode = document.createTextNode(line || '\u200B')
							range.insertNode(textNode)
							range.setStartAfter(textNode)
							range.collapse(true)
							selection.removeAllRanges()
							selection.addRange(range)
						}

						restoreLatexElements(currentBlock)
						const pasteKey = `${noteUid.value}-${targetUid}`
						recentlyPastedBlocks.add(pasteKey)
						await socket.value.modifyNoteBlock(noteUid.value, targetUid, currentBlock.innerHTML, 'text')
						setTimeout(() => {
							recentlyPastedBlocks.delete(pasteKey)
						}, 1000)
					} else {
						const blockUid = `temp-${useCommonStore().uniqueId()}`
						const lineContent = line || '\u200B'
						const mergedContent = index === 1 && tailHtml ? `${lineContent}${tailHtml}` : lineContent

						const tempBlockData = {
							type: 'text',
							content: mergedContent,
							blockUid: blockUid,
						}

						blockData.push(tempBlockData)

						const newBlock = document.createElement('div')
						const newContainer = document.createElement('div')

						newContainer.classList.add('editor--block__container')

						newBlock.classList.add('editor--block')
						newBlock.dataset.type = 'text'
						newBlock.setAttribute('contenteditable', 'true')
						newBlock.textContent = mergedContent
						newBlock.id = blockUid

						newContainer.appendChild(newBlock)

						let containerToInsertAfter = textBlockContainer.closest('.editor--block__container')
						if (!containerToInsertAfter && initialContainer) {
							containerToInsertAfter = initialContainer
						}
						if (containerToInsertAfter) {
							containerToInsertAfter.insertAdjacentElement('afterend', newContainer)
							textBlockContainer = newContainer
							const normalizedContent = mergedContent.replace(/[\s\u00A0\u200B-\u200D\uFEFF]/g, '')
							if (normalizedContent && normalizedContent.trim() !== '') {
								lastBlockToFocus = newBlock
							}
						}
					}
				}
			}

			if (htmlElementBlob) {
				const parsedBody = parser.parseFromString(htmlElementBlob, 'text/html').body

				// XSS/인젝션 방어: 위험한 태그를 실행 불가능한 텍스트로 변환
				const isInsideLatex = (el: Element) => !!el.closest('[data-latex]')

				const dangerousTags = ['script', 'iframe', 'object', 'embed', 'style', 'link', 'form', 'input', 'textarea', 'select', 'button', 'meta', 'base', 'applet', 'noscript', 'template', 'svg']
				dangerousTags.forEach(tag => {
					parsedBody.querySelectorAll(tag).forEach(el => {
						if (isInsideLatex(el)) return
						const textNode = document.createTextNode(el.outerHTML)
						el.parentNode?.replaceChild(textNode, el)
					})
				})
				parsedBody.querySelectorAll('.katex').forEach(el => {
					if (isInsideLatex(el)) return
					const katexHtml = el.querySelector('.katex-html')
					const visibleText = (katexHtml?.textContent || el.textContent || '').trim()
					const normalize = (s: string) => s.replace(/\s+/g, '')

					const next = el.nextSibling
					if (next && next.nodeType === Node.TEXT_NODE) {
						const nextText = (next.textContent || '').trim()
						if (nextText && visibleText && normalize(nextText) === normalize(visibleText)) {
							el.remove()
							return
						}
					}

					const textNode = document.createTextNode(visibleText)
					el.parentNode?.replaceChild(textNode, el)
				})
				parsedBody.querySelectorAll('math').forEach(el => {
					if (isInsideLatex(el)) return
					el.remove()
				})
				parsedBody.querySelectorAll('*').forEach(el => {
					const attrs = [...el.attributes]
					for (const attr of attrs) {
						const name = attr.name.toLowerCase()
						if (name.startsWith('on')) {
							el.removeAttribute(attr.name)
							continue
						}
						const value = attr.value.toLowerCase().replace(/\s/g, '')
						if (
							(name === 'href' || name === 'src' || name === 'action' || name === 'formaction' || name === 'xlink:href') &&
							(value.startsWith('javascript:') || value.startsWith('vbscript:') || value.startsWith('data:text/html'))
						) {
							el.removeAttribute(attr.name)
						}
					}
				})

				const isInsideTable = block?.dataset?.type === 'defaultTable' || !!(event.target as HTMLElement)?.closest?.('.editor--block[data-type="defaultTable"]')
				if (isInsideTable) {
					const tableBlocks = parsedBody.querySelectorAll('.editor--block[data-type="defaultTable"]')
					tableBlocks.forEach((tableBlock: Element) => {
						const textContent = tableBlock.textContent || ''
						const textNode = document.createTextNode(textContent)
						tableBlock.parentNode?.replaceChild(textNode, tableBlock)
					})
					const tables = parsedBody.querySelectorAll('table')
					tables.forEach((table: Element) => {
						const textContent = table.textContent || ''
						const textNode = document.createTextNode(textContent)
						table.parentNode?.replaceChild(textNode, table)
					})
				}

				const hasBr = htmlElementBlob.toLowerCase().includes('<br')
				// <br> 태그가 있고 .editor--block이 없으면 <br>을 줄바꿈으로 변환
				const hasEditorBlock = parsedBody.querySelector('.editor--block')

				// 이미지나 복잡한 구조가 있는지 확인
				const hasComplexContent =
					parsedBody.querySelector('img') ||
					parsedBody.querySelector('table') ||
					parsedBody.querySelector('figure') ||
					parsedBody.querySelector('iframe') ||
					parsedBody.querySelector('video')

				const hasRichFormatting =
					parsedBody.querySelector('span[style]') ||
					parsedBody.querySelector('strong') ||
					parsedBody.querySelector('em') ||
					parsedBody.querySelector('b') ||
					parsedBody.querySelector('i') ||
					parsedBody.querySelector('u') ||
					parsedBody.querySelector('s') ||
					parsedBody.querySelector('sub') ||
					parsedBody.querySelector('sup') ||
					parsedBody.querySelector('a') ||
					parsedBody.querySelector('h1') ||
					parsedBody.querySelector('h2') ||
					parsedBody.querySelector('h3') ||
					parsedBody.querySelector('h4') ||
					parsedBody.querySelector('h5') ||
					parsedBody.querySelector('h6') ||
					parsedBody.querySelector('p[style]')

				if (hasBr && !hasEditorBlock && !hasComplexContent && !hasRichFormatting) {
					const brElements = parsedBody.querySelectorAll('br')
					brElements.forEach(br => {
						const textNode = document.createTextNode('\n')
						br.parentNode?.replaceChild(textNode, br)
					})
					const textContent = parsedBody.textContent || ''
					await insertPlainTextLines(textContent)
				} else {
					// 전처리 1: 블록 레벨 콘텐츠를 감싸는 wrapper div 언래핑
					const blockLevelTags = ['DIV', 'P', 'UL', 'OL', 'TABLE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'FIGURE']
					let unwrapChanged = true
					while (unwrapChanged) {
						unwrapChanged = false
						Array.from(parsedBody.children).forEach((child: any) => {
							if (child.tagName === 'DIV' && (!child.classList || child.classList.length === 0)) {
								const hasBlockChildren = Array.from(child.children).some((grandChild: any) => blockLevelTags.includes(grandChild.tagName))
								if (hasBlockChildren) {
									while (child.firstChild) {
										parsedBody.insertBefore(child.firstChild, child)
									}
									child.remove()
									unwrapChanged = true
								}
							}
						})
					}

					// <ul>, <ol> 태그를 각 <li>를 블록으로 변환 (중첩 리스트 포함)
					let hasListConversion = false

					// <li>의 inline 콘텐츠를 추출하여 블록 div를 생성
					// 중첩 리스트(<ul>/<ol>)는 제외하고 나머지 자식 노드(텍스트, span 등)를 보존
					function createBlockFromLi(li: Element): HTMLElement | null {
						const div = document.createElement('div')
						div.classList.add('editor--block')
						div.setAttribute('data-type', 'text')
						div.setAttribute('data-from-list', 'true')

						Array.from(li.childNodes).forEach((node: any) => {
							if (node.nodeType === Node.ELEMENT_NODE && (node.tagName === 'UL' || node.tagName === 'OL')) {
								return
							}
							div.appendChild(node.cloneNode(true))
						})

						const text = div.textContent?.trim() || ''
						if (text) {
							hasListConversion = true
							return div
						}
						return null
					}

					// 재귀적으로 모든 li를 순회하여 평탄화 (depth-first 순서)
					function flattenList(element: Element, blockDivs: HTMLElement[]) {
						const children = Array.from(element.children)
						children.forEach((child: any) => {
							if (child.tagName === 'LI') {
								const div = createBlockFromLi(child)
								if (div) blockDivs.push(div)

								Array.from(child.children).forEach((nestedChild: any) => {
									if (nestedChild.tagName === 'UL' || nestedChild.tagName === 'OL') {
										flattenList(nestedChild, blockDivs)
									}
								})
							} else if (child.tagName === 'UL' || child.tagName === 'OL') {
								flattenList(child, blockDivs)
							}
						})
					}

					// 1. parsedBody의 직접 자식 <li> 태그들 처리
					const directLiElements = Array.from(parsedBody.children).filter((child: any) => child.tagName === 'LI')
					directLiElements.forEach((li: any) => {
						const div = createBlockFromLi(li)
						if (div) {
							parsedBody.insertBefore(div, li)
						}

						Array.from(li.children).forEach((nestedChild: any) => {
							if (nestedChild.tagName === 'UL' || nestedChild.tagName === 'OL') {
								const nestedDivs: HTMLElement[] = []
								flattenList(nestedChild, nestedDivs)
								nestedDivs.forEach(d => parsedBody.insertBefore(d, li))
							}
						})

						li.remove()
					})

					// 2. <ul>, <ol> 태그 내부의 <li> 처리 (최상위 레벨만)
					// 변환된 블록을 원래 리스트 위치에 삽입하여 순서 유지
					const topLevelLists = Array.from(parsedBody.children).filter((child: any) => child.tagName === 'UL' || child.tagName === 'OL')
					topLevelLists.forEach((list: any) => {
						const blockDivs: HTMLElement[] = []
						flattenList(list, blockDivs)
						blockDivs.forEach(div => {
							parsedBody.insertBefore(div, list)
						})
						list.remove()
					})

					let docList: Array<Element> = [...parsedBody.children]
					let originalDocListLength = docList.length // 선언 위치 이동

					// TextNode 처리:
					// 1. META 태그를 제외한 Element가 없는 경우 (TextNode만 있는 경우)
					// 2. SPAN만 있지만 TextNode도 섞여있는 경우 (inline 스타일)
					const nonMetaElements = docList.filter(el => el.tagName !== 'META')
					const hasOnlyMetaTag = nonMetaElements.length === 0 && docList.some(el => el.tagName === 'META')
					const hasTextContent = (parsedBody.textContent?.length || 0) > 0

					// TextNode가 있는지 확인 (META 제외, 공백도 포함)
					const hasTextNodes = Array.from(parsedBody.childNodes).some(node => node.nodeType === Node.TEXT_NODE && node.textContent)

					// SPAN만 있는 경우도 체크 (inline 스타일)
					const hasOnlySpans = nonMetaElements.length > 0 && nonMetaElements.every(el => el.tagName === 'SPAN')

					if ((nonMetaElements.length === 0 || hasOnlyMetaTag || (hasOnlySpans && hasTextNodes)) && hasTextContent) {
						const wrapperDiv = document.createElement('div')
						wrapperDiv.classList.add('editor--block')

						Array.from(parsedBody.childNodes).forEach(node => {
							if (node.nodeType !== Node.ELEMENT_NODE || (node as Element).tagName !== 'META') {
								wrapperDiv.appendChild(node.cloneNode(true))
							}
						})

						// 텍스트 노드 내 \n을 <br>로 변환 (white-space: normal에서 줄바꿈 보존)
						convertNewlinesToBr(wrapperDiv)

						// 공백만 있는 경우도 허용 (trim 제거)
						if ((wrapperDiv.textContent?.length || 0) > 0) {
							// 공백만 있는 경우 pre 스타일 적용
							if (wrapperDiv.textContent && wrapperDiv.textContent.trim() === '') {
								wrapperDiv.style.whiteSpace = 'pre'
							}
							parsedBody.innerHTML = ''
							parsedBody.appendChild(wrapperDiv)
							docList = [wrapperDiv]
							originalDocListLength = 1 // 병합 후 업데이트
						} else {
						}
					}

					// .editor--container__content가 복사된 경우 (전체 에디터가 복사됨)
					let editorContainer: any = parsedBody.querySelector('.editor--container__content')
					if (editorContainer) {
						const blockContainers = editorContainer.querySelectorAll('.editor--block__container')

						// 단일 블록만 있는 경우 parsedBody를 해당 블록으로 교체하여 inline 삽입 로직 활용
						if (blockContainers.length === 1) {
							const singleBlock = blockContainers[0].querySelector('.editor--block')
							if (singleBlock) {
								parsedBody.innerHTML = ''
								parsedBody.appendChild(singleBlock.cloneNode(true))
								docList = [parsedBody.children[0]]
								editorContainer = null // inline 삽입 로직이 실행되도록 무효화
								originalDocListLength = 1 // 병합 후 업데이트
							} else {
								docList = []
							}
						} else {
							// 여러 블록인 경우
							const blocks = Array.from(blockContainers)
								.map((container: any) => container.querySelector('.editor--block'))
								.filter(Boolean) as Array<Element>

							// 텍스트 정규화 함수: 공백 및 zero-width 문자 제거
							const normalizeText = (text: string) => text.replace(/[\s\u00A0\u200B-\u200D\uFEFF]/g, '')

							// paste 이벤트 중복 방어는 isPasting 플래그로 처리되므로 별도 중복 제거 불필요
							const dedupedBlocks = blocks

							// 브라우저 아티팩트 감지
							if (dedupedBlocks.length > 1) {
								// 케이스 1: 빈 블록 체크
								const allOthersEmpty = dedupedBlocks.slice(1).every((block: Element) => {
									const blockText = normalizeText(block.textContent || '')
									return blockText === ''
								})

								// 케이스 2: 짧은 inline 텍스트 (100자 이하 + 복잡한 구조 없음)
								const totalText = dedupedBlocks.map(b => b.textContent || '').join('')
								const hasComplexStructure = dedupedBlocks.some(
									(el: any) =>
										el.querySelector?.('table') ||
										el.querySelector?.('img') ||
										el.querySelector?.('figure') ||
										el.querySelector?.('iframe') ||
										el.querySelector?.('video') ||
										el.nodeName === 'IMG',
								)
								// 모든 요소가 .editor--block인 경우는 의도적인 블록 구분이므로 병합하지 않음
								const allAreEditorBlocks = dedupedBlocks.every((el: any) => el.classList?.contains('editor--block'))
								const isShortInlineText = totalText.length <= 100 && !hasComplexStructure && !allAreEditorBlocks

								// hasComplexStructure가 true면 병합하지 않음 (이미지/테이블 등)
								if ((allOthersEmpty || isShortInlineText) && !hasComplexStructure) {
									// 브라우저 아티팩트로 판단, inline 병합
									parsedBody.innerHTML = ''
									const tempBlock = document.createElement('div')
									tempBlock.classList.add('editor--block')

									dedupedBlocks.forEach((block: any) => {
										if (block.classList?.contains('editor--block')) {
											// .editor--block인 경우: childNodes만 추출 (블록 래퍼 제거)
											Array.from(block.childNodes).forEach((child: any) => {
												tempBlock.appendChild(child.cloneNode(true))
											})
										} else {
											// 일반 요소(span 등)인 경우: 요소 자체를 복사 (스타일 보존)
											tempBlock.appendChild(block.cloneNode(true))
										}
									})

									parsedBody.appendChild(tempBlock)
									docList = [parsedBody.children[0]]
									editorContainer = null
								} else {
									docList = dedupedBlocks
								}
							} else {
								docList = dedupedBlocks
							}
						}
					} else {
						// .editor--block__container가 복사된 경우, 안의 .editor--block만 추출
						docList = docList
							.map((child: any) => {
								if (child.classList && child.classList.contains('editor--block__container')) {
									return child.querySelector('.editor--block') || child
								}
								return child
							})
							.filter(Boolean) as Array<Element>

						// wrapper div 안에 .editor--block이 있는 경우 추출
						const finalDocList: Element[] = []
						docList.forEach((item: any) => {
							// item이 일반 div이고 .editor--block을 자식으로 가지는 경우
							if (item.tagName === 'DIV' && !item.classList.contains('editor--block')) {
								const innerBlocks = item.querySelectorAll('.editor--block')
								if (innerBlocks.length > 0) {
									// wrapper div 안의 .editor--block들을 추출
									innerBlocks.forEach((block: Element) => finalDocList.push(block))
								} else {
									const directChildDivs = Array.from(item.children).filter((child: any) => child.tagName === 'DIV')
									const hasNoDirectText = Array.from(item.childNodes).every((node: any) => node.nodeType !== Node.TEXT_NODE || !node.textContent?.trim())
									if (directChildDivs.length > 1 && hasNoDirectText) {
										directChildDivs.forEach((div: any) => finalDocList.push(div))
									} else {
										finalDocList.push(item)
									}
								}
							} else {
								finalDocList.push(item)
							}
						})
						docList = finalDocList
					}

					// docList에서 독립된 <br> 요소 제거 (블록 구분자 역할만 하므로 빈 블록 생성 방지)
					let hadBrSeparator = false
					docList = docList.filter((item: any) => {
						if (item.nodeType === Node.ELEMENT_NODE && (item as Element).tagName === 'BR') {
							hadBrSeparator = true
							return false
						}
						return true
					})

					// 원본 docList 길이 업데이트 (병합 후)
					originalDocListLength = docList.length

					// 브라우저 아티팩트 감지: 첫 블록에만 내용이 있고 나머지는 모두 빈 블록인 경우
					// 단, 리스트에서 변환된 경우는 병합하지 않음
					if (docList.length > 1 && !hasListConversion) {
						const normalizeText = (text: string) => text.replace(/[\s\u00A0\u200B-\u200D\uFEFF]/g, '')

						// 케이스 1: 빈 블록 체크
						const firstText = normalizeText(docList[0]?.textContent || '')
						const allOthersEmpty = docList.slice(1).every((item: Element) => {
							const itemText = normalizeText(item.textContent || '')
							return itemText === ''
						})

						// 케이스 2: 짧은 inline 텍스트 (100자 이하 + 복잡한 구조 없음)
						const totalText = docList.map(el => el.textContent || '').join('')
						const hasComplexStructure = docList.some(
							(el: any) =>
								el.querySelector?.('table') ||
								el.querySelector?.('img') ||
								el.querySelector?.('figure') ||
								el.querySelector?.('iframe') ||
								el.querySelector?.('video') ||
								el.nodeName === 'IMG',
						)
						// 모든 요소가 .editor--block인 경우는 의도적인 블록 구분이므로 병합하지 않음
						const allAreEditorBlocks = docList.every((el: any) => el.classList?.contains('editor--block'))
						// 빈 블록이 중간에 포함된 경우는 의도적인 블록 구분이므로 병합하지 않음
						const hasEmptyBlockInMiddle = docList.some((el: Element, idx: number) => {
							if (idx === 0 || idx === docList.length - 1) return false // 첫/마지막 제외
							const text = normalizeText(el.textContent || '')
							return text === ''
						})
						const hasMultipleParagraphs = docList.filter((el: any) => el.tagName === 'P').length > 1
						const isShortInlineText = totalText.length <= 100 && !hasComplexStructure && !allAreEditorBlocks && !hasEmptyBlockInMiddle && !hasMultipleParagraphs && !hadBrSeparator
						// hasComplexStructure가 true면 병합하지 않음 (이미지/테이블 등)
						if (allOthersEmpty && firstText !== '' && !hasComplexStructure) {
							// 브라우저 아티팩트: 첫 블록만 유지
							docList = [docList[0]]
							originalDocListLength = 1 // 병합 후 업데이트
						} else if (isShortInlineText) {
							// 짧은 inline 텍스트 병합
							parsedBody.innerHTML = ''
							const tempBlock = document.createElement('div')
							tempBlock.classList.add('editor--block')

							docList.forEach((block: any) => {
								if (block.classList?.contains('editor--block')) {
									// .editor--block인 경우: childNodes만 추출 (블록 래퍼 제거)
									Array.from(block.childNodes).forEach((child: any) => {
										tempBlock.appendChild(child.cloneNode(true))
									})
								} else {
									// 일반 요소(span 등)인 경우: 요소 자체를 복사 (스타일 보존)
									tempBlock.appendChild(block.cloneNode(true))
								}
							})

							parsedBody.appendChild(tempBlock)
							docList = [parsedBody.children[0]]
							originalDocListLength = 1 // 병합 후 업데이트
						}
					}

					// 크로스 블록 선택 감지 및 정규화: 선택이 여러 블록에 걸쳐 있으면 단일 블록으로 병합
					if (selection.rangeCount > 0) {
						const normalizeRange = selection.getRangeAt(0)
						if (!normalizeRange.collapsed) {
							const startBlockEl = (
								normalizeRange.startContainer.nodeType === Node.ELEMENT_NODE ? (normalizeRange.startContainer as HTMLElement) : normalizeRange.startContainer.parentElement
							)?.closest('.editor--block') as HTMLElement | null
							const endBlockEl = (
								normalizeRange.endContainer.nodeType === Node.ELEMENT_NODE ? (normalizeRange.endContainer as HTMLElement) : normalizeRange.endContainer.parentElement
							)?.closest('.editor--block') as HTMLElement | null

							if (startBlockEl && endBlockEl && startBlockEl !== endBlockEl) {
								const { noteUid } = storeToRefs(useEditorStore())

								// 끝 블록에서 선택 영역 이후의 tail 내용 저장
								const endTailRange = document.createRange()
								endTailRange.setStart(normalizeRange.endContainer, normalizeRange.endOffset)
								if (endBlockEl.lastChild) {
									endTailRange.setEndAfter(endBlockEl.lastChild)
								} else {
									endTailRange.setEnd(endBlockEl, endBlockEl.childNodes.length)
								}
								const tailFragment = endTailRange.cloneContents()

								// 시작 블록에서 선택 시작점 이후의 내용 삭제
								const startDeleteRange = document.createRange()
								startDeleteRange.setStart(normalizeRange.startContainer, normalizeRange.startOffset)
								if (startBlockEl.lastChild) {
									startDeleteRange.setEndAfter(startBlockEl.lastChild)
								} else {
									startDeleteRange.setEnd(startBlockEl, startBlockEl.childNodes.length)
								}
								startDeleteRange.deleteContents()

								// 삭제할 블록 UID 수집 및 중간/끝 블록 컨테이너 제거
								const removedBlockUids: string[] = []
								const startContainerEl = startBlockEl.closest('.editor--block__container')
								const endContainerEl = endBlockEl.closest('.editor--block__container')
								if (startContainerEl && endContainerEl && startContainerEl !== endContainerEl) {
									let current = startContainerEl.nextElementSibling
									while (current) {
										const next = current.nextElementSibling
										const blockInContainer = current.querySelector('.editor--block')
										if (blockInContainer?.id) {
											removedBlockUids.push(deletePrefix(blockInContainer.id))
										}
										const isEnd = current === endContainerEl
										current.remove()
										if (isEnd) break
										current = next
									}
								}

								// 끝 블록의 tail 내용을 시작 블록에 추가
								startBlockEl.appendChild(tailFragment)

								// block 참조가 시작 블록이 아닌 경우 업데이트
								if (block !== startBlockEl) {
									block = startBlockEl
									targetUid = deletePrefix(startBlockEl.id)
									newBlockContainer = block
									initialContainer = block?.closest('.editor--block__container')
								}

								// 서버에 삭제된 블록 알림
								if (removedBlockUids.length > 0 && noteType.value === 'Project') {
									socket.value.deleteNoteBlockList(noteUid.value, removedBlockUids)
								}

								// 커서를 삽입 위치(tail 앞)로 설정
								const newCursorRange = document.createRange()
								newCursorRange.setStart(startDeleteRange.startContainer, startDeleteRange.startOffset)
								newCursorRange.collapse(true)
								selection.removeAllRanges()
								selection.addRange(newCursorRange)
							}
						}
					}

					// 블록 내 일부 텍스트 선택 복사 감지 및 inline 삽입 처리
					// 원본이 1개 블록일 때만 inline 삽입 (2개 이상 블록이 병합된 경우 제외)
					if (!editorContainer && parsedBody.children.length === 1 && originalDocListLength === 1) {
						const firstChild = parsedBody.children[0]
						const isEditorBlock = firstChild.classList?.contains('editor--block')
						const hasTable = firstChild.querySelector('table')
						const blockContainers = firstChild.querySelectorAll('.editor--block__container')
						const hasMultipleContainers = blockContainers.length > 1

						const isSimpleDiv = firstChild.tagName === 'DIV' && !firstChild.classList?.length
						const isSimpleSpan = firstChild.tagName === 'SPAN'
						if ((isEditorBlock || isSimpleDiv || isSimpleSpan) && !hasTable && !hasMultipleContainers) {
							// 부모로부터 상속된 불필요한 스타일 제거
							const tempDiv = document.createElement('div')
							// .editor--block의 순수 내용만 추출 (childNodes 직접 사용)
							const editorBlockEl = parsedBody.querySelector('.editor--block')
							let sourceElement: HTMLElement
							if (editorBlockEl) {
								sourceElement = editorBlockEl as HTMLElement
							} else if (firstChild.tagName === 'SPAN') {
								// span인 경우 parsedBody 사용 (TextNode + span 섞여있을 수 있음)
								sourceElement = parsedBody
							} else {
								sourceElement = firstChild as HTMLElement
							}

							// innerHTML 대신 childNodes 직접 복사
							Array.from(sourceElement.childNodes).forEach((node: Node) => {
								tempDiv.appendChild(node.cloneNode(true))
							})

							// 텍스트 노드 내 \n을 <br>로 변환 (white-space: normal에서 줄바꿈 보존)
							convertNewlinesToBr(tempDiv)

							// 부모에서 넘어오는 스타일 제거, 서식 관련 스타일만 보존
							const allowedStyles = [
								'font-weight',
								'font-style',
								'text-decoration',
								'text-decoration-line',
								'text-decoration-style',
								'text-decoration-color',
								'text-decoration-thickness',
								'color',
								'background-color',
								'font-size',
								'vertical-align',
							]

							const allSpans = tempDiv.querySelectorAll('span')
							allSpans.forEach((el: any) => {
								// 1. 허용된 스타일만 보존
								if (el.style && el.style.length > 0) {
									const preservedStyles: Array<{ name: string; value: string; priority: string }> = []
									allowedStyles.forEach(styleName => {
										const value = el.style.getPropertyValue(styleName)
										const priority = el.style.getPropertyPriority(styleName)
										if (value) {
											preservedStyles.push({ name: styleName, value, priority })
										}
									})

									// Clear all styles first
									el.style.cssText = ''

									// Re-apply only allowed styles with their original priority
									preservedStyles.forEach(({ name, value, priority }) => {
										el.style.setProperty(name, value, priority)
									})

									if (preservedStyles.length === 0) {
										el.removeAttribute('style')
									}
								}

								// 2. 폰트 패밀리 class 보존 (editor-로 시작하지 않는 class)
								if (el.classList && el.classList.length > 0) {
									const preservedClasses: string[] = []
									for (let i = 0; i < el.classList.length; i++) {
										const cls = el.classList[i]
										if (cls && !cls.startsWith('editor-')) {
											preservedClasses.push(cls)
										}
									}
									if (preservedClasses.length > 0) {
										el.className = preservedClasses.join(' ')
									} else {
										el.removeAttribute('class')
									}
								}
							})

							// childNodes 직접 복사로 공백 보존
							const fragment = document.createDocumentFragment()
							Array.from(tempDiv.childNodes).forEach((node: Node) => {
								fragment.appendChild(node.cloneNode(true))
							})

							// 빈 내용 체크
							if (fragment.childNodes.length === 0) {
								isPasting.value = false
								return
							}

							// inline 콘텐츠 삽입
							if (selection.rangeCount > 0) {
								const { noteUid } = storeToRefs(useEditorStore())
								const range = selection.getRangeAt(0)
								range.deleteContents()

								// range가 target block 내부에 있는지 검증
								const rangeNode = range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.parentElement : (range.startContainer as HTMLElement)
								const rangeBlock = rangeNode?.closest?.('.editor--block')
								const isRangeInsideBlock = rangeBlock === block

								if (isRangeInsideBlock) {
									// 캐럿이 span 안에 있으면 span을 분할하여 중첩 방지
									let container = range.startContainer
									let offset = range.startOffset

									// 캐럿이 속한 span 찾기
									let targetSpan: HTMLSpanElement | null = null
									if (container.nodeType === Node.ELEMENT_NODE) {
										const el = container as HTMLElement
										if (el.tagName === 'SPAN') {
											targetSpan = el as HTMLSpanElement
										}
									} else if (container.nodeType === Node.TEXT_NODE) {
										const parent = container.parentElement
										if (parent && parent.tagName === 'SPAN') {
											targetSpan = parent as HTMLSpanElement
										}
									}

									// span 안에 캐럿이 있으면 span을 분할
									if (targetSpan && targetSpan.parentElement && targetSpan.parentElement.tagName !== 'SPAN') {
										const grandParent = targetSpan.parentElement

										if (container.nodeType === Node.TEXT_NODE) {
											const textNode = container as Text
											const textContent = textNode.textContent || ''
											const beforeText = textContent.substring(0, offset)
											const afterText = textContent.substring(offset)

											// 분할 지점 찾기
											let foundSplit = false
											const beforeContents: Node[] = []
											const afterContents: Node[] = []

											Array.from(targetSpan.childNodes).forEach(child => {
												if (foundSplit) {
													if (child.nodeType === Node.TEXT_NODE) {
														afterContents.push(document.createTextNode(child.textContent || ''))
													} else {
														afterContents.push(child.cloneNode(true))
													}
												} else if (child === textNode) {
													if (beforeText) {
														beforeContents.push(document.createTextNode(beforeText))
													}
													if (afterText) {
														afterContents.push(document.createTextNode(afterText))
													}
													foundSplit = true
												} else {
													if (child.nodeType === Node.TEXT_NODE) {
														beforeContents.push(document.createTextNode(child.textContent || ''))
													} else {
														beforeContents.push(child.cloneNode(true))
													}
												}
											})

											// 삽입 위치 마커
											const insertMarker = document.createElement('span')
											insertMarker.setAttribute('data-split-marker', 'true')

											// before span 생성
											if (beforeContents.length > 0) {
												const beforeSpan = targetSpan.cloneNode(false) as HTMLSpanElement
												beforeContents.forEach(c => beforeSpan.appendChild(c))
												grandParent.insertBefore(beforeSpan, targetSpan)
											}

											// 마커 삽입
											grandParent.insertBefore(insertMarker, targetSpan)

											// after span 생성
											if (afterContents.length > 0) {
												const afterSpan = targetSpan.cloneNode(false) as HTMLSpanElement
												afterContents.forEach(c => afterSpan.appendChild(c))
												grandParent.insertBefore(afterSpan, targetSpan)
											}

											targetSpan.remove()

											// range를 마커 위치로 설정
											range.selectNode(insertMarker)
											range.collapse(false)
										} else if (container.nodeType === Node.ELEMENT_NODE && container === targetSpan) {
											if (offset === targetSpan.childNodes.length) {
												range.setStartAfter(targetSpan)
												range.collapse(true)
											} else if (offset === 0) {
												range.setStartBefore(targetSpan)
												range.collapse(true)
											}
										}
									}
								}

								const markerSpan = document.createElement('span')
								markerSpan.setAttribute('data-paste-caret-marker', 'true')
								fragment.appendChild(markerSpan)

								// void 요소(br, img 등)는 자식을 가질 수 없으므로 range를 부모 레벨로 보정
								const VOID_TAGS = ['BR', 'IMG', 'HR', 'INPUT', 'WBR', 'COL', 'EMBED']
								if (range.startContainer.nodeType === Node.ELEMENT_NODE && VOID_TAGS.includes((range.startContainer as HTMLElement).tagName)) {
									const voidEl = range.startContainer as HTMLElement
									const parent = voidEl.parentNode
									if (parent) {
										const idx = Array.from(parent.childNodes).indexOf(voidEl)
										range.setStart(parent, idx)
										range.collapse(true)
									}
								}

								if (isRangeInsideBlock) {
									range.insertNode(fragment)
								} else {
									const br = block.querySelector('br')
									if (br) {
										block.insertBefore(fragment, br)
									} else if (block.firstChild) {
										block.insertBefore(fragment, block.firstChild)
									} else {
										block.appendChild(fragment)
									}
								}

								// 분할 마커 제거
								const splitMarker = block.querySelector('span[data-split-marker]')
								if (splitMarker) {
									splitMarker.remove()
								}

								// 중첩 span 정리 함수: 내부 span 태그만 제거하고 내용은 유지
								const unwrapNestedSpans = () => {
									let nestedSpan = block.querySelector('span span')
									let safetyCounter = 0
									while (nestedSpan && safetyCounter < 100) {
										safetyCounter++
										const inner = nestedSpan as HTMLSpanElement
										const outer = inner.parentElement
										if (!outer || outer.tagName !== 'SPAN') {
											nestedSpan = block.querySelector('span span')
											continue
										}

										// 마커인 경우 스킵하고 다음 찾기
										if (inner.hasAttribute('data-paste-caret-marker')) {
											const nextNested = block.querySelectorAll('span span')
											let found = false
											for (let i = 0; i < nextNested.length; i++) {
												if (!nextNested[i].hasAttribute('data-paste-caret-marker')) {
													nestedSpan = nextNested[i]
													found = true
													break
												}
											}
											if (!found) break
											continue
										}

										// 내부 span의 내용을 외부 span으로 이동
										while (inner.firstChild) {
											outer.insertBefore(inner.firstChild, inner)
										}
										inner.remove()

										nestedSpan = block.querySelector('span span')
									}
								}

								unwrapNestedSpans()

								// 커서를 삽입된 콘텐츠 끝으로 이동
								const marker = block.querySelector('span[data-paste-caret-marker]')
								if (marker) {
									const cursorRange = document.createRange()
									cursorRange.setStartAfter(marker)
									cursorRange.collapse(true)
									selection.removeAllRanges()
									selection.addRange(cursorRange)
									marker.remove()
								}

								restoreLatexElements(block)
								const pasteKey = `${noteUid.value}-${targetUid}`
								recentlyPastedBlocks.add(pasteKey)
								socket.value.modifyNoteBlock(noteUid.value, targetUid, block.innerHTML, 'text')
								setTimeout(() => {
									recentlyPastedBlocks.delete(pasteKey)
								}, 1000)
							}

							// 붙여넣기 종료
							await nextTick()
							newBlockEvent()
							isPasting.value = false
							new UseSelection().setChangeSelection()
							await nextTick()
							return
						}
					}

				// 복사된 블록이 2개 이상일 때는 커서 뒤 텍스트를 떼어내어 첫 새 블록에 붙인다.
				let tailHtml: any = ''
				if (docList.length > 1 && selection.rangeCount > 0) {
					const editorBlock = block
					if (editorBlock) {
						const caretRange = selection.getRangeAt(0)
						const tailRange = caretRange.cloneRange()
						if (editorBlock.lastChild) {
							tailRange.setEndAfter(editorBlock.lastChild)
						} else {
							tailRange.setEnd(editorBlock, editorBlock.childNodes.length)
						}
						const container = document.createElement('div')
						container.appendChild(tailRange.cloneContents())
						tailHtml = container.innerHTML
						// 원본 블록에서 커서 뒤 내용을 제거해 중복을 방지
						tailRange.deleteContents()
					}
				}

					let previewUsed = false
					for (let index = 0; index < docList.length; index++) {
						const childNode: any = docList[index]
						let blockUid = `temp-${useCommonStore().uniqueId()}`
						let parent = childNode.closest('table')

						if (!parent) {
							parent = childNode.querySelector('table')
						}

					if (parent) {
						let table: any
						if (parent.closest('.editor--block')) {
							table = setTableData(parent)
						} else {
							table = setTableTextData(parent)
						}

					const tempBlockData = {
						type: 'defaultTable',
						content: {
							type: 'defaultTable',
							tableUID: useCommonStore().uniqueId(),
							...table,
						},
						blockUid: blockUid,
					}

						let tableTailHtml = ''
						if (index === 0 && block && selection.rangeCount > 0) {
							const caretRange = selection.getRangeAt(0)

							if (!caretRange.collapsed) {
								caretRange.deleteContents()
							}

							if (block.lastChild) {
								const tableTailRange = document.createRange()
								tableTailRange.setStart(caretRange.startContainer, caretRange.startOffset)
								tableTailRange.setEndAfter(block.lastChild)
								const tc = document.createElement('div')
								tc.appendChild(tableTailRange.cloneContents())
								tableTailHtml = tc.innerHTML
								tableTailRange.deleteContents()
							}

							const normText = (text: string) => text.replace(/[\s\u00A0\u200B-\u200D\uFEFF]/g, '')
							if (normText(block.textContent || '') !== '' && noteType.value === 'Project') {
								const { noteUid } = storeToRefs(useEditorStore())
								restoreLatexElements(block)
								const pasteKey = `${noteUid.value}-${targetUid}`
								recentlyPastedBlocks.add(pasteKey)
								socket.value.modifyNoteBlock(noteUid.value, targetUid, block.innerHTML, 'text')
								setTimeout(() => {
									recentlyPastedBlocks.delete(pasteKey)
								}, 1000)
							}
						}

						blockData.push(tempBlockData)

						const htmlTable = useCreateTable().insertTable(tempBlockData.content, blockUid)
						if (block) {
							let newBlock: any = document.createElement('div')
							let newContainer: any = document.createElement('div')
							newContainer.classList.add('editor--block__container')

							newBlock.classList.add('editor--block')
							newBlock.dataset.type = 'defaultTable'
							newBlock.contentEditable = 'false'
							newBlock.innerHTML = htmlTable
							newBlock.id = blockUid

							newContainer.appendChild(newBlock)

							let containerToInsertAfter = newBlockContainer.closest('.editor--block__container')
							if (!containerToInsertAfter && initialContainer) {
								containerToInsertAfter = initialContainer
							}
							if (containerToInsertAfter) {
								containerToInsertAfter.insertAdjacentElement('afterend', newContainer)
								newBlockContainer = newContainer
								lastBlockToFocus = newBlock
							}
						}

						useCreateTable().event()

						if (tableTailHtml) {
							const checkDiv = document.createElement('div')
							checkDiv.innerHTML = tableTailHtml
							const normText = (text: string) => text.replace(/[\s\u00A0\u200B-\u200D\uFEFF]/g, '')
							if (normText(checkDiv.textContent || '') !== '') {
								const tailBlockUid = `temp-${useCommonStore().uniqueId()}`
								blockData.push({
									type: 'text',
									content: tableTailHtml,
									blockUid: tailBlockUid,
								})

								const tailBlock = document.createElement('div')
								const tailContainer = document.createElement('div')
								tailContainer.classList.add('editor--block__container')
								tailBlock.classList.add('editor--block')
								tailBlock.dataset.type = 'text'
								tailBlock.setAttribute('contenteditable', 'true')
								tailBlock.innerHTML = tableTailHtml
								tailBlock.id = tailBlockUid
								tailContainer.appendChild(tailBlock)

								let containerToInsertAfter = newBlockContainer.closest('.editor--block__container')
								if (!containerToInsertAfter && initialContainer) {
									containerToInsertAfter = initialContainer
								}
								if (containerToInsertAfter) {
									containerToInsertAfter.insertAdjacentElement('afterend', tailContainer)
									newBlockContainer = tailContainer
									lastBlockToFocus = tailBlock
								}
							}
						}
						} else {
							// LBI-502: innerHTML을 우선 사용하여 포맷팅 보존 (superscript 등)
							// 중첩된 .editor--block이 있으면 제거
							let enters = childNode.innerHTML || childNode.textContent || ''
							const innerBlock = childNode.querySelector('.editor--block')
							if (innerBlock) {
								// .editor--block__container를 제거하고 순수한 내용만 추출
								const tempDiv = document.createElement('div')
								tempDiv.innerHTML = innerBlock.innerHTML || ''

								// 중첩된 .editor--block__container 제거
								const nestedContainers = tempDiv.querySelectorAll('.editor--block__container')
								nestedContainers.forEach((container: any) => {
									const nestedBlock = container.querySelector('.editor--block')
									if (nestedBlock) {
										// 컨테이너를 그 내용(블록의 innerHTML)으로 교체
										const fragment = document.createDocumentFragment()
										Array.from(nestedBlock.childNodes).forEach((node: any) => {
											fragment.appendChild(node.cloneNode(true))
										})
										container.replaceWith(fragment)
									} else {
										container.remove()
									}
								})

								enters = tempDiv.innerHTML || innerBlock.textContent || ''
							}

							if (childNode.nodeName === 'IMG' || childNode.querySelectorAll('img').length > 0) {
								let imgList: any
								if (childNode.nodeName === 'IMG') {
									imgList = [childNode]
								} else {
									imgList = [...childNode.querySelectorAll('img')]
								}
								for (const img of imgList) {
									const imgBase64 = img.getAttribute('src')
									const dimensions = await getImageDimensions(imgBase64, img)

									const tempBlockData = {
										type: 'image',
										content: `<div class="image--container" style="width: ${dimensions.width}px;" data-height="${dimensions.height}"><img style="width: ${dimensions.width}px;" data-height="${dimensions.height}" src="${imgBase64}" alt="image" /></div>`,
										tempBlockUid: blockUid,
									}

									if (block) {
										let newBlock: any = document.createElement('div')
										let newContainer: any = document.createElement('div')
										newContainer.classList.add('editor--block__container')
										if (!checkBlock() || previewUsed) {
											newBlock.classList.add('editor--block')
											newBlock.dataset.type = 'image'
											newBlock.contentEditable = 'false'
											newBlock.innerHTML = tempBlockData.content
											newBlock.id = blockUid

											newContainer.appendChild(newBlock)

											newBlockContainer.closest('.editor--block__container').insertAdjacentElement('afterend', newContainer)
											newBlockContainer = newContainer
											lastBlockToFocus = newBlock
											previewUsed = true

											blockData.push(tempBlockData)
										} else {
											block.dataset.type = 'image'
											block.classList.remove('active')
											block.contentEditable = 'false'
											block.innerHTML = tempBlockData.content
											newBlockContainer = block
											previewUsed = true

											if (noteType.value === 'Project') {
												const { noteUid } = storeToRefs(useEditorStore())
												const pasteKey = `${noteUid.value}-${targetUid}`
												recentlyPastedBlocks.add(pasteKey)
												socket.value.modifyNoteBlock(noteUid.value, targetUid, tempBlockData.content, 'image')
												setTimeout(() => {
													recentlyPastedBlocks.delete(pasteKey)
												}, 1000)
											}
										}
									}
								}
							} else {
								if (index === 0) {
									if (selection.rangeCount > 0) {
										const { noteUid } = storeToRefs(useEditorStore())
										const editorBlock = block

										if (editorBlock) {
											// 스타일 필터링 적용
											const tempDiv = document.createElement('div')
											tempDiv.innerHTML = enters

											// 부모에서 넘어오는 스타일 제거, 서식 관련 스타일만 보존
											const allowedStyles = [
												'font-weight',
												'font-style',
												'text-decoration',
												'text-decoration-line',
												'text-decoration-style',
												'text-decoration-color',
												'text-decoration-thickness',
												'color',
												'background-color',
												'font-size',
												'vertical-align',
											]

											const allSpans = tempDiv.querySelectorAll('span')
											allSpans.forEach((el: any) => {
												// 1. 허용된 스타일만 보존
												if (el.style && el.style.length > 0) {
													const preservedStyles: string[] = []
													allowedStyles.forEach(styleName => {
														const value = el.style.getPropertyValue(styleName)
														if (value) {
															preservedStyles.push(`${styleName}: ${value}`)
														}
													})
													const styles = preservedStyles.join('; ')

													if (styles) {
														el.style.cssText = styles
													} else {
														el.removeAttribute('style')
													}
												}

												// 2. 폰트 패밀리 class 보존 (editor-로 시작하지 않는 class)
												if (el.classList && el.classList.length > 0) {
													const preservedClasses: string[] = []
													for (let i = 0; i < el.classList.length; i++) {
														const cls = el.classList[i]
														if (cls && !cls.startsWith('editor-')) {
															preservedClasses.push(cls)
														}
													}
													if (preservedClasses.length > 0) {
														el.className = preservedClasses.join(' ')
													} else {
														el.removeAttribute('class')
													}
												}
											})

											// childNodes 직접 복사로 공백 보존
											const fragment = document.createDocumentFragment()
											Array.from(tempDiv.childNodes).forEach((node: Node) => {
												fragment.appendChild(node.cloneNode(true))
											})

											const range = selection.getRangeAt(0)
											range.deleteContents()
											const last = fragment.lastChild
											range.insertNode(fragment)
											if (last) {
												range.setStartAfter(last)
												range.collapse(true)
												selection.removeAllRanges()
												selection.addRange(range)
											}
										}

										const pasteKey = `${noteUid.value}-${targetUid}`
										recentlyPastedBlocks.add(pasteKey)
										await socket.value.modifyNoteBlock(noteUid.value, targetUid, editorBlock.innerHTML, 'text')
										setTimeout(() => {
											recentlyPastedBlocks.delete(pasteKey)
										}, 1000)

										// 단일 블록 붙여넣기인 경우 현재 블록을 lastBlockToFocus로 설정
										if (docList.length === 1) {
											const tempDiv = document.createElement('div')
											tempDiv.innerHTML = editorBlock.innerHTML
											const textContent = tempDiv.textContent || ''
											const normalizedContent = textContent.replace(/[\s\u00A0\u200B-\u200D\uFEFF]/g, '')
											if (normalizedContent && normalizedContent.trim() !== '') {
												lastBlockToFocus = editorBlock
											}
										}
									}
								} else {
									// 두 번째 블록부터는 커서 뒤쪽 텍스트를 첫 새 블록에만 붙인다.
									const mergedContent = index === 1 && tailHtml ? `${enters}${tailHtml}` : enters
									const tempBlockData = {
										type: 'text',
										content: mergedContent,
										blockUid: blockUid,
									}

									blockData.push(tempBlockData)

									const newBlock = document.createElement('div')
									const newContainer = document.createElement('div')

									newContainer.classList.add('editor--block__container')

									newBlock.classList.add('editor--block')
									newBlock.dataset.type = 'text'
									newBlock.setAttribute('contenteditable', 'true')
									newBlock.innerHTML = mergedContent
									newBlock.id = blockUid

									newContainer.appendChild(newBlock)

									let containerToInsertAfter = newBlockContainer.closest('.editor--block__container')
									if (!containerToInsertAfter && initialContainer) {
										containerToInsertAfter = initialContainer
									}
									if (containerToInsertAfter) {
										containerToInsertAfter.insertAdjacentElement('afterend', newContainer)
										newBlockContainer = newContainer
										// 빈 블록이 아닌 경우에만 lastBlockToFocus 설정
										const tempDiv = document.createElement('div')
										tempDiv.innerHTML = mergedContent
										const textContent = tempDiv.textContent || ''
										const normalizedContent = textContent.replace(/[\s\u00A0\u200B-\u200D\uFEFF]/g, '')
										if (normalizedContent && normalizedContent.trim() !== '') {
											lastBlockToFocus = newBlock
										}
									}
								}
							}
						}
					}

					// 마지막 블록 저장 (lastBlockToFocus가 아직 설정되지 않은 경우에만)
					if (!lastBlockToFocus && newBlockContainer && newBlockContainer !== block) {
						const lastBlock = newBlockContainer.querySelector('.editor--block') || newBlockContainer
						if (lastBlock) {
							const tempDiv = document.createElement('div')
							tempDiv.innerHTML = lastBlock.innerHTML || ''
							const textContent = tempDiv.textContent || ''
							const normalizedContent = textContent.replace(/[\s\u00A0\u200B-\u200D\uFEFF]/g, '')
							if (normalizedContent && normalizedContent.trim() !== '') {
								selector.value = lastBlock
								lastBlockToFocus = lastBlock
							}
						}
					}
				}
			} else if (textFromEvent && textFromEvent.trim() !== '') {
				await insertPlainTextLines(textFromEvent)
			} else if (imageList.length > 0) {
				const block: any = document.getElementById(selector.value.id) as HTMLElement
				for (const img of imageList) {
					let imgBase64: string | any = null
					if (img.includes('blob:')) {
						const resp = await fetch(img)
						const blob = await resp.blob()
						imgBase64 = await blobToDataURL(blob)
					} else {
						imgBase64 = img.getAttribute('src')
					}

					let blockUid = `temp-${useCommonStore().uniqueId()}`
					const dimensions = await getImageDimensions(imgBase64)

					const tempBlockData = {
						type: 'image',
						content: `<div class="image--container" style="width: ${dimensions.width}px;" data-height="${dimensions.height}"><img style="width: ${dimensions.width}px;" data-height="${dimensions.height}" src="${imgBase64}" alt="image" /></div>`,
						tempBlockUid: blockUid,
					}

					if (block) {
						let newBlock: any = document.createElement('div')
						let newContainer: any = document.createElement('div')
						newContainer.classList.add('editor--block__container')
						if (!checkBlock()) {
							newBlock.classList.add('editor--block')
							newBlock.dataset.type = 'image'
							newBlock.contentEditable = 'false'
							newBlock.innerHTML = tempBlockData.content
							newBlock.id = blockUid

							newContainer.appendChild(newBlock)

							block.closest('.editor--block__container').insertAdjacentElement('afterend', newContainer)

							blockData.push(tempBlockData)
						} else {
							block.dataset.type = 'image'
							block.classList.remove('active')
							block.contentEditable = 'false'
							block.innerHTML = tempBlockData.content
						}
					}
				}
			}

			let pasteTargetUid = targetUid

			// 테이블 붙여넣기 후 원본 블록이 비어있으면 DOM에서 숨기기 (서버 삭제는 paste 응답 후)
			const hasTableInBlockData = blockData.some((item: any) => item.type === 'defaultTable')
			let shouldDeleteOriginalBlock = false
			if (hasTableInBlockData && block) {
				const normText = (text: string) => text.replace(/[\s\u00A0\u200B-\u200D\uFEFF]/g, '')
				if (normText(block.textContent || '') === '') {
					shouldDeleteOriginalBlock = true
					const emptyContainer = block.closest('.editor--block__container') as HTMLElement
					if (emptyContainer) {
						emptyContainer.style.display = 'none'
					}
				}
			}

			if (noteType.value === 'Project') {
				socket.value.pasteBlock(pasteTargetUid, blockData)
				// pasteBlock emit 이후에 원본 블록 삭제 요청 (순서 보장)
				if (shouldDeleteOriginalBlock && !targetUid.startsWith('temp')) {
					const { noteUid } = storeToRefs(useEditorStore())
					setTimeout(() => {
						socket.value.deleteNoteBlockList(noteUid.value, [targetUid])
					}, 100)
				}
			}

			await nextTick()
			newBlockEvent()
		} catch (pasteError) {
			throw pasteError
		} finally {
			// 이벤트 리스너 등록 후 포커스
			if (lastBlockToFocus) {
				await nextTick()

				// 모든 블록의 active 클래스 제거
				const editorBlockList = [...document.querySelectorAll('.editor--block')]
				editorBlockList.forEach((block: any) => {
					block.classList.remove('active')
				})

				selector.value = lastBlockToFocus

				if (lastBlockToFocus.dataset.type === 'text') {
					// 텍스트 블록이면 커서 유지
					lastBlockToFocus.classList.add('active')
					lastBlockToFocus.focus()

					await nextTick()

					const range = document.createRange()
					const sel = window.getSelection()
					if (lastBlockToFocus.lastChild) {
						range.setStartAfter(lastBlockToFocus.lastChild)
					} else {
						range.selectNodeContents(lastBlockToFocus)
					}
					range.collapse(false)
					sel?.removeAllRanges()
					sel?.addRange(range)

					await nextTick()

					const useSelection = new UseSelection()
					useSelection.setChangeSelection()
				} else {
					// 테이블/이미지 등이면 선택 해제
					window.getSelection()?.removeAllRanges()
					;(document.activeElement as HTMLElement)?.blur()
				}
			}

			isPasting.value = false

			// 붙여넣기 완료 후 keyCount 리셋 (saveCurrentStateIfNeeded에서 중복 저장 방지)
			if (typeof window !== 'undefined' && (window as any).keyCountManager) {
				;(window as any).keyCountManager.resetKeyCount()
			}
		}
	}
	const blobToDataURL = async (blob: Blob) =>
		new Promise<string>((resolve, reject) => {
			const reader = new FileReader()
			reader.onload = () => resolve(reader.result as string)
			reader.onerror = reject
			reader.readAsDataURL(blob)
		})

	if (typeof window !== 'undefined') {
		;(window as any).__testPaste = async (text: string, html?: string) => {
			const mockEvent = {
				preventDefault: () => {},
				stopPropagation: () => {},
				clipboardData: {
					items: [],
					getData: (type: string) => {
						if (type === 'text/plain') return text
						if (type === 'text/html') return html || ''
						return ''
					},
				},
			}

			return await onPaste(mockEvent)
		}
	}

	return {
		onPaste,
	}
}
